import { useEffect, useState } from 'react'
import useGroupDetail from './useGroupDetail'
import useGroupSubscriber, { PAGE_LIMIT } from './useGroupSubscriber'
import { filter, isEmpty, isNil, map, mergeLeft, prop } from 'ramda'
import { Routes } from 'app/services/routeService/routeService'
import TitlePage from 'app/componentV4/TitlePage'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import {
  FormControl,
  IconButton,
  InputAdornment,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import SearchIcon from '@material-ui/icons/Search'
import { useIntl, FormattedMessage } from 'react-intl'
import AccessControl from 'app/auth/AccessControl'
import { AccessType } from 'app/auth/accessControlService'
import { TwoPersonAddIcon } from 'app/components/icons'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import { SECONDARY } from 'app/constants/button'
import ComposeMessageButton from 'app/componentV4/Button/ComposeMessageButton'
import { isEmptyOrNil } from 'libs/empty'
import clsx from 'clsx'
import Assets from 'app/configs/assetConfigs'
import DataTable from 'app/componentV4/Table/DataTable'
import { ColumnOfSubscriberData } from './ColumnOfSubscriberData'
import { Skeleton } from '@material-ui/lab'
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd'
import useGroup from './useGroup'
import { useGroupDialog } from './contexts/GroupDialogContext'
import { DIALOG_KEYS } from './constants'
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import { usePlace } from 'commons/usePlace'
import SubscriberExportCSV from './dialogs/SubscriberExportCSV'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import LimitTooltip from 'app/componentV4/Tooltip/LimitTooltip'
import { useAccessControl } from 'app/auth/useAccessControl'
import { SUB_GROUP_MAX } from 'app/constants/group'

const useStyle = makeStyles((theme) => ({
  table: {
    '&.data-table': {
      [theme.breakpoints.up('md')]: {
        height: 'calc(100vh - 150px)',
      },
    },
  },
}))

export default (props) => {
  const {
    match: {
      params: { groupId },
    },
    history,
  } = props
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const place = usePlace()
  // const [totalSelectedIds, setTotalSelectedIds] = useState([])
  // const [selectedIdsMap, setSelectedIdsMap] = useState({})
  const [selectedIds, setSelectedIds] = useState([])
  const [isResetSelected, setIsResetSelected] = useState(false)
  const isDownXs = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyle()

  const { isGranted: hasPermissionCreateAndDelete } = useAccessControl([
    AccessType.ADMINISTRATION,
    AccessType.SUBSCRIBER_UPDATE,
  ])

  const {
    state: { group, isLoading, error, updateAt, subGroupSelected },
    getGroupDetail,
    onSelectSubgroup,
    reFetchGroupDetail,
  } = useGroupDetail()

  const {
    addSubgroup,
    editGroupName,
    deleteGroupByIds,
    editSubGroupName,
    deleteSubgroup,
    openCompose,
    deleteSubgroupFromGroup,
    deleteSubgroupFromTag,
    addSubscriberToTags,
  } = useGroup()
  const { openDialog, setLoading, closeDialog } = useGroupDialog()

  const {
    state: {
      searchText,
      isLoading: isLoadingSubscribers,
      isEmptySubscribers,
      subscribers,
      meta,
      params,
    },
    handleFetchSubscribersForGroup,
    onSearchSubscriber,
    onSearchText,
    setSubscriberLoading,
  } = useGroupSubscriber()

  const isLastPage = meta.currentPage === meta.lastPage
  const isEmptyDetail = !isLoading && isEmpty(group) && !isNil(updateAt)

  // const handleSelected = (value, flatValue = []) => {
  //   setSelectedIdsMap(value)
  //   setTotalSelectedIds(flatValue)
  // }
  const handleSelected = (value, values = []) => {
    const subscriberIds = Object.values(value).flat()
    setSelectedIds(subscriberIds.concat(values))
  }

  const onClearData = () => {
    // setSelectedIdsMap({})
    // setTotalSelectedIds([])
    setSelectedIds({})
    setIsResetSelected(true)
  }

  const openAddSubgroupDialog = (group) => {
    openDialog(DIALOG_KEYS.SUB_GROUP_ADD, {
      group,
      onSubmit: ({ data, resetData, setFieldError }) => {
        setLoading(DIALOG_KEYS.SUB_GROUP_ADD, true)
        addSubgroup(data)
          .then((res) => {
            setLoading(DIALOG_KEYS.SUB_GROUP_ADD, false)
            closeDialog(DIALOG_KEYS.SUB_GROUP_ADD)
            resetData()
            reFetchGroupDetail({ groupId })
          })
          .catch((error) => {
            const code = error?.response?.data?.details?.code
            if (code === 610) {
              setFieldError(
                'name',
                formatMessage({ id: 'error.400.subgroupNameExists' }),
              )
            }
          })
          .finally(() => {
            setLoading(DIALOG_KEYS.SUB_GROUP_ADD, false)
          })
      },
    })
  }

  const openEditNameGroupDialog = (group) => {
    openDialog(DIALOG_KEYS.GROUP_EDIT_NAME, {
      group,
      onSubmit: ({ data, setFieldError }) => {
        setLoading(DIALOG_KEYS.GROUP_EDIT_NAME, true)
        editGroupName(data)
          .then((res) => {
            setLoading(DIALOG_KEYS.GROUP_EDIT_NAME, false)
            closeDialog(DIALOG_KEYS.GROUP_EDIT_NAME)
            reFetchGroupDetail({ groupId })
          })
          .catch((error) => {
            const code = error?.response?.data?.details?.code
            if (code === 608) {
              setFieldError(
                'name',
                formatMessage({ id: 'error.400.groupNameExists' }),
              )
            }
          })
          .finally(() => {
            setLoading(DIALOG_KEYS.GROUP_EDIT_NAME, false)
          })
      },
    })
  }

  const onOpenDeleteGroupDialog = (groupIds, type) => {
    openDialog(DIALOG_KEYS.GROUP_DELETE, {
      type,
      onSubmit: () => {
        setLoading(DIALOG_KEYS.GROUP_DELETE, true)
        deleteGroupByIds({
          groupIds,
          placeId: place.id,
        })
          .then((res) => {
            setLoading(DIALOG_KEYS.GROUP_DELETE, false)
            closeDialog(DIALOG_KEYS.GROUP_DELETE)
            history.push(Routes.groups())
          })
          .catch(() => closeDialog(DIALOG_KEYS.GROUP_DELETE))
      },
    })
  }

  const openEditSubgroupNameDialog = (tag) => {
    openDialog(DIALOG_KEYS.SUB_GROUP_EDIT_NAME, {
      tag,
      onSubmit: ({ data, setFieldError }) => {
        setLoading(DIALOG_KEYS.SUB_GROUP_EDIT_NAME, true)
        editSubGroupName(data)
          .then((res) => {
            setLoading(DIALOG_KEYS.SUB_GROUP_EDIT_NAME, false)
            closeDialog(DIALOG_KEYS.SUB_GROUP_EDIT_NAME)
            reFetchGroupDetail({ groupId })
          })
          .catch((error) => {
            const code = error?.response?.data?.details?.code
            if (code === 610) {
              setFieldError(
                'name',
                formatMessage({ id: 'error.400.subgroupNameExists' }),
              )
            }
          })
          .finally(() => {
            setLoading(DIALOG_KEYS.SUB_GROUP_EDIT_NAME, false)
          })
      },
    })
  }

  const openDeleteSubGroupDialog = (tagId) => {
    openDialog(DIALOG_KEYS.SUB_GROUP_DELETE, {
      onSubmit: () => {
        setLoading(DIALOG_KEYS.SUB_GROUP_DELETE, true)
        deleteSubgroup({ tagId })
          .then((res) => {
            setLoading(DIALOG_KEYS.SUB_GROUP_DELETE, false)
            closeDialog(DIALOG_KEYS.SUB_GROUP_DELETE)
            if (subGroupSelected.id === tagId) {
              const newtags = filter((item) => item.id !== tagId, group.tags)
              onSelectSubgroup(newtags[0])
            }
            reFetchGroupDetail({ groupId })
          })
          .catch(() => closeDialog(DIALOG_KEYS.SUB_GROUP_DELETE))
      },
    })
  }

  const openDeleteSubscriberFromGroupDialog = ({ groupId, subscriberIds }) => {
    openDialog(DIALOG_KEYS.DELETE_SUBSCRIBER_FROM_GROUP, {
      subscriberIds,
      onSubmit: () => {
        setLoading(DIALOG_KEYS.DELETE_SUBSCRIBER_FROM_GROUP, true)
        const deleteFunc = isEmptyOrNil(subGroupSelected)
          ? deleteSubgroupFromGroup({ groupId, subscriberIds })
          : deleteSubgroupFromTag({
              tagId: subGroupSelected?.id,
              subscriberIds,
            })

        deleteFunc
          .then((res) => {
            setLoading(DIALOG_KEYS.DELETE_SUBSCRIBER_FROM_GROUP, false)
            closeDialog(DIALOG_KEYS.DELETE_SUBSCRIBER_FROM_GROUP)
            reFetchGroupDetail({ groupId })

            let newParams = mergeLeft(
              {
                limit: meta.perPage,
                page: 1,
                groupId,
                tagIds: isNil(subGroupSelected)
                  ? group.tags.map((tag) => tag.id)
                  : [subGroupSelected?.id],
                keyword: searchText,
              },
              params,
            )
            const countOnLastPage =
              meta?.total % meta?.perPage === 0
                ? meta?.perPage
                : meta?.total % meta?.perPage
            if (
              isLastPage &&
              countOnLastPage === subscriberIds.length &&
              meta.currentPage !== 1
            ) {
              newParams = mergeLeft(
                { placeId: place.id, page: meta?.lastPage - 1 },
                params,
              )
            }

            handleFetchSubscribersForGroup(newParams)
            onClearData()
          })
          .catch(() => closeDialog(DIALOG_KEYS.DELETE_SUBSCRIBER_FROM_GROUP))
      },
    })
  }

  const openAddSubscriberToSubGroupDialog = (group, tagId) => {
    onSearchText('')
    openDialog(DIALOG_KEYS.ADD_SUBSCRIBER, {
      name: subGroupSelected.name,
      tagId,
      onSubmit: ({ subscriberIds }) => {
        setLoading(DIALOG_KEYS.ADD_SUBSCRIBER, true)
        addSubscriberToTags({ tagId, subscriberIds }).then((res) => {
          setLoading(DIALOG_KEYS.ADD_SUBSCRIBER, false)
          closeDialog(DIALOG_KEYS.ADD_SUBSCRIBER)
          getGroupDetail({ groupId })
          handleFetchSubscribersForGroup({
            limit: meta.perPage,
            page: 1,
            groupId,
            tagIds: isNil(subGroupSelected)
              ? group.tags.map((tag) => tag.id)
              : [subGroupSelected?.id],
            keyword: searchText,
          })
        }) // If fail hold dialog
      },
    })
  }

  const renderBulkActions = () => {
    // const selectedIdsArray = selectedIds[meta.currentPage] || []
    // const totalSelectedCount = selectedIdsArray.length

    if (selectedIds === 0) return null

    return (
      <div className="divide-x divide-neutrals-400 flex items-center">
        <SubscriberExportCSV
          selectedIds={selectedIds} // Need summary of selected
          totalSelectedCount={selectedIds.length}
        />
        {hasPermissionCreateAndDelete && (
          <div className="divide-x divide-neutrals-400 flex">
            <div className="pl-5">
              <Typography
                className="text-14 font-400 text-red-500 cursor-pointer"
                onClick={() =>
                  openDeleteSubscriberFromGroupDialog({
                    groupId,
                    subscriberIds: selectedIds, // change from per page selectedIds[meta.currentPage] to totalSelectedIds
                  })
                }
              >
                {`${formatMessage({
                  id: 'action.remove',
                })} (${selectedIds.length})`}
              </Typography>
            </div>
          </div>
        )}
      </div>
    )
  }

  useEffect(() => {
    setSubscriberLoading()
    getGroupDetail({ groupId })
  }, [])

  useEffect(() => {
    if (subGroupSelected) {
      onSearchText('')
      handleFetchSubscribersForGroup({
        limit: PAGE_LIMIT,
        page: 1,
        groupId,
        tagIds: isNil(subGroupSelected)
          ? group.tags.map((tag) => tag.id)
          : [subGroupSelected?.id],
        keyword: '',
      })
    }
  }, [subGroupSelected])

  useEffect(() => {
    if (isResetSelected) setIsResetSelected(false)
  }, [isResetSelected])

  useEffect(() => {
    if (!isNil(error) && !isLoading) {
      history.push(Routes.groups())
    }
  }, [error])

  return (
    <div className="p-24 h-full overflow-auto md:overflow-hidden">
      <div className="w-full space-y-5 md:space-y-0 md:flex justify-between space-y-3">
        <div className="flex items-center">
          {!isEmptyDetail && (
            <TitlePage
              label={
                <div className="flex space-x-4 text-blue-950">
                  <div
                    className="flex items-center cursor-pointer"
                    onClick={() => {
                      history.push(Routes.groups())
                    }}
                  >
                    <FormattedMessage id="groups.header.label" />
                  </div>
                  <div className="flex items-center">
                    <ChevronRightIcon fontSize="small" />
                  </div>
                  <div className="flex items-center">
                    {isLoading ? (
                      <Skeleton
                        animation="wave"
                        variant="text"
                        className="w-64"
                      />
                    ) : (
                      <div
                        className="w-full cursor-pointer"
                        onClick={() => {
                          onSelectSubgroup(undefined)
                          onSearchText('')
                          setIsResetSelected(true)
                          handleFetchSubscribersForGroup({
                            limit: PAGE_LIMIT,
                            page: 1,
                            groupId,
                            keyword: '',
                            tagIds: group.tags.map((tag) => tag.id),
                          })
                        }}
                      >
                        <LimitTooltip
                          maxWidth={150}
                          title={group.name}
                          className="truncate"
                        />
                      </div>
                    )}
                  </div>
                  {!isNil(subGroupSelected) && (
                    <div className="flex items-center">
                      <ChevronRightIcon fontSize="small" />
                    </div>
                  )}

                  <div className="flex items-center">
                    {isLoading ? (
                      <Skeleton
                        animation="wave"
                        variant="text"
                        className="w-64"
                      />
                    ) : (
                      subGroupSelected && (
                        <LimitTooltip
                          maxWidth={100}
                          title={subGroupSelected?.name}
                          className="truncate"
                        />
                      )
                    )}
                  </div>
                </div>
              }
            />
          )}
        </div>
        <div className="space-y-5 md:space-y-0 md:space-x-10 md:flex items-center">
          {!isEmptySubscribers && (
            <FormControl variant="outlined" className="w-full md:w-256">
              <SimpleInput
                id="search-subscriber-on-group"
                name="search"
                placeholder={formatMessage({
                  id: 'input.searchSubscriber',
                })}
                value={searchText}
                fullWidth
                size="small"
                onChange={(event) => {
                  onSearchText(event.target.value)
                  onSearchSubscriber(
                    {
                      limit: meta.perPage,
                      page: 1,
                      groupId,
                      tagIds: isNil(subGroupSelected)
                        ? group.tags.map((tag) => tag.id)
                        : [subGroupSelected?.id],
                      keyword: event.target.value,
                    },
                    onClearData,
                  )
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon className="text-grey-400" />
                  </InputAdornment>
                }
              />
            </FormControl>
          )}
          {!isEmptySubscribers && (
            <div className="w-full md:w-fit">
              <AccessControl
                accesses={[
                  AccessType.ADMINISTRATION,
                  AccessType.SUBSCRIBER_UPDATE,
                ]}
              >
                {isEmptyOrNil(subGroupSelected) ? (
                  <PrimaryTooltip
                    title={formatMessage({
                      id: 'groupsv4.add.subgroup.tooltip.text',
                    })}
                  >
                    <div>
                      <OutlinedButtons
                        id="open-add-subscriber-tag-empty"
                        color={SECONDARY}
                        disabled={isEmptyOrNil(subGroupSelected)}
                        type="button"
                        className="w-full capitalize"
                        onClick={() => {}}
                      >
                        <div className="flex">
                          <TwoPersonAddIcon classes="w-18 mx-2" />
                          {formatMessage({
                            id: 'groupsv4.detail.addSubgroup.member.text',
                          })}
                        </div>
                      </OutlinedButtons>
                    </div>
                  </PrimaryTooltip>
                ) : (
                  <OutlinedButtons
                    id="openAddSubscriberToTagEmpty"
                    color={SECONDARY}
                    onClick={() => {
                      openAddSubscriberToSubGroupDialog(
                        group,
                        subGroupSelected?.id,
                      )
                    }}
                    type="button"
                    className="w-full capitalize"
                  >
                    <div className="flex">
                      <TwoPersonAddIcon classes="w-18 mx-2" />
                      {formatMessage({
                        id: 'groupsv4.detail.addSubgroup.member.text',
                      })}
                    </div>
                  </OutlinedButtons>
                )}
              </AccessControl>
            </div>
          )}

          <AccessControl
            accesses={[AccessType.NOTIFICATION_SEND, AccessType.ADMINISTRATION]}
          >
            <ComposeMessageButton
              onClick={() => {
                const tagIds = isEmptyOrNil(subGroupSelected)
                  ? map((item) => item.id, group.tags)
                  : [subGroupSelected?.id]
                openCompose(tagIds)
              }}
            />
          </AccessControl>
        </div>
      </div>
      <div className="sm:flex space-x-0 space-y-5 sm:space-y-0 sm:space-x-5 mt-5">
        <div className="w-full sm:w-3/12 h-218 sm:h-auto">
          <div className="bg-white md:max-w-md rounded-8 p-4 py-5 text-blue-950">
            {isLoading ? (
              <Skeleton
                animation="wave"
                variant="text"
                className="w-full h-40"
              />
            ) : (
              <div
                className={clsx(
                  'flex group justify-between px-5 text-18 font-medium py-6 cursor-pointer h-40 text-blue-950',
                  isNil(subGroupSelected) && 'bg-primary-350 rounded-4',
                )}
                onClick={() => {
                  onSelectSubgroup(undefined)
                  onSearchText('')
                  setIsResetSelected(true)
                  handleFetchSubscribersForGroup({
                    limit: PAGE_LIMIT,
                    page: 1,
                    groupId,
                    keyword: '',
                    tagIds: group.tags.map((tag) => tag.id),
                  })
                }}
              >
                <div className="flex">
                  <LimitTooltip
                    maxWidth={130}
                    title={group.name}
                    className="truncate"
                  />
                  <div className="px-2">({group.subscriberCount})</div>
                </div>
                <AccessControl accesses={[AccessType.ADMINISTRATION]}>
                  <div className="hidden group-hover:flex items-center space-x-2">
                    <IconButton
                      type="button"
                      size="small"
                      className="text-blue-950 hover:underline"
                      onClick={(event) => {
                        event.stopPropagation()
                        openEditNameGroupDialog(group)
                      }}
                    >
                      <CreateOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      type="button"
                      className="text-red hover:underline"
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpenDeleteGroupDialog([group.id], 'menu')
                      }}
                    >
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  </div>
                </AccessControl>
              </div>
            )}
            <AccessControl accesses={[AccessType.ADMINISTRATION]}>
              {isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="text"
                  className="w-full h-40"
                />
              ) : (
                <div
                  className="flex py-4 px-5 space-x-4 text-secondary cursor-pointer"
                  onClick={() =>
                    group.tagCount >= SUB_GROUP_MAX
                      ? openDialog(DIALOG_KEYS.GROUP_LIMIT, {
                          description: formatMessage({
                            id: 'groupv4.dialog.subgroup.limit.description',
                          }),
                        })
                      : openAddSubgroupDialog(group)
                  }
                >
                  <PlaylistAddIcon fontSize="small" />
                  <Typography className="text-14 font-400">
                    <FormattedMessage id="group.details.dialogs.addSubgroup" />
                  </Typography>
                </div>
              )}
            </AccessControl>
            <div
              className="py-4 space-y-4 overflow-y-auto h-288 sm:h-auto"
              style={{ maxHeight: '69vh' }}
            >
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton
                    animation="wave"
                    variant="text"
                    className="w-full h-40"
                  />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    className="w-full h-40"
                  />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    className="w-full h-40"
                  />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    className="w-full h-40"
                  />
                </div>
              ) : (
                !isEmptyOrNil(group) &&
                map(
                  (tag) => {
                    return (
                      <div
                        className={clsx(
                          'flex group justify-between py-6 text-blue-950 text-14 px-5 cursor-pointer h-40 items-center',
                          tag.id === subGroupSelected?.id &&
                            'bg-primary-350 rounded-4 font-600',
                        )}
                        key={`Subgroup${tag.id}`}
                        onClick={() => {
                          onSelectSubgroup(tag)
                          onSearchText('')
                          setIsResetSelected(true)
                        }}
                      >
                        <div className="flex">
                          <div style={{ maxWidth: '200px' }}>
                            <LimitTooltip
                              maxWidth={150}
                              title={tag.name}
                              className="truncate"
                            />
                          </div>
                          <div className="px-2">({tag.subscriberCount})</div>
                        </div>
                        <AccessControl accesses={[AccessType.ADMINISTRATION]}>
                          <div className="hidden group-hover:flex items-center space-x-2">
                            <IconButton
                              type="button"
                              size="small"
                              className="text-blue-950 hover:underline"
                              onClick={(event) => {
                                event.stopPropagation()
                                openEditSubgroupNameDialog(tag)
                              }}
                            >
                              <CreateOutlinedIcon fontSize="small" />
                            </IconButton>
                            {group?.tags.length > 1 && (
                              <IconButton
                                size="small"
                                type="button"
                                className="text-red hover:underline"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  openDeleteSubGroupDialog(tag.id)
                                }}
                              >
                                <DeleteOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </div>
                        </AccessControl>
                      </div>
                    )
                  },
                  prop('tags', group),
                )
              )}
            </div>
          </div>
        </div>
        <div className="w-full sm:w-9/12">
          {!isLoadingSubscribers &&
          !isLoading &&
          isEmptySubscribers &&
          isEmptyOrNil(searchText) ? (
            <div>
              <div className="space-y-5 mt-128">
                <div className="w-full flex justify-center">
                  <img
                    src={Assets.groupv4EmptySubscriberIcon}
                    alt="Empty subscriber on group"
                  />
                </div>
                <div className="w-full flex justify-center font-bold text-16">
                  <FormattedMessage id="groupsv4.detail.subscriber.member.empty.title" />
                </div>
                <div className="w-full flex justify-center">
                  <FormattedMessage id="groupsv4.detail.subscriber.empty.body" />
                </div>
              </div>
              <div className="w-full flex justify-center mt-10">
                <AccessControl
                  accesses={[
                    AccessType.ADMINISTRATION,
                    AccessType.SUBSCRIBER_UPDATE,
                  ]}
                >
                  {isEmptyOrNil(subGroupSelected) ? (
                    <PrimaryTooltip
                      title={formatMessage({
                        id: 'groupsv4.add.subgroup.tooltip.text',
                      })}
                    >
                      <div>
                        <OutlinedButtons
                          id="add-subgroup-disabled"
                          color={SECONDARY}
                          disabled={isEmptyOrNil(subGroupSelected)}
                          type="button"
                          className="w-full capitalize"
                          onClick={() => {}}
                        >
                          <div className="flex">
                            <TwoPersonAddIcon classes="w-18 mx-2" />
                            {formatMessage({
                              id: 'groupsv4.detail.addSubgroup.member.text',
                            })}
                          </div>
                        </OutlinedButtons>
                      </div>
                    </PrimaryTooltip>
                  ) : (
                    <OutlinedButtons
                      id="open-add-subscriber-tag"
                      color={SECONDARY}
                      onClick={() => {
                        openAddSubscriberToSubGroupDialog(
                          group,
                          subGroupSelected?.id,
                        )
                      }}
                      type="button"
                      className="capitalize"
                    >
                      <div className="flex">
                        <TwoPersonAddIcon classes="w-18 mx-2" />
                        {formatMessage({
                          id: 'groupsv4.detail.addSubgroup.member.text',
                        })}
                      </div>
                    </OutlinedButtons>
                  )}
                </AccessControl>
              </div>
            </div>
          ) : (
            <DataTable
              data={subscribers}
              isCheckbox={true}
              className={classes.table}
              columns={ColumnOfSubscriberData(
                isDownXs,
                openDeleteSubscriberFromGroupDialog,
                formatMessage,
                group,
                hasPermissionCreateAndDelete,
              )}
              isResetAllSelected={isResetSelected}
              pagination={meta}
              sticky={isDownXs ? 'checkBox' : 'firstName'}
              isLoading={isLoading || isLoadingSubscribers}
              customSelectedCount={selectedIds.length}
              onChange={(value, values) => {
                handleSelected(value, values)
              }}
              onFetch={(value) => {
                handleFetchSubscribersForGroup(
                  mergeLeft(
                    {
                      ...value,
                      groupId,
                      tagIds: isNil(subGroupSelected)
                        ? group.tags.map((tag) => tag.id)
                        : [subGroupSelected?.id],
                      keyword: searchText,
                    },
                    params,
                  ),
                )
              }}
              bulkAction={renderBulkActions()}
            />
          )}
        </div>
      </div>
    </div>
  )
}
